import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminTopline } from "../../components/console";
import { Plus, X } from "../../components/icons";
import { api, authHeaders, describeError } from "../../lib/api";
import { getCurrentScope } from "../../lib/auth";
import {
  EXPENSE_CAPTION, EXPENSE_CATEGORIES, EXPENSE_CATEGORY_LO, type Expense, type ExpenseCategory, type ExpenseWrite,
} from "../../lib/finance-report";
import { fullLAK, num } from "../../lib/format";
import { PERM, hasPerm } from "../../lib/permissions";
import { dmy } from "../../lib/stats-range";
import { FinanceFilters } from "./FinanceFilters";
import { useFinanceFilters, usePlaceName, type FinanceFiltersState } from "./filters";

/**
 * ລາຍຈ່າຍ — รายการรายจ่ายในเขต + บันทึก/ยกเลิก (ADR-0034)
 *
 * บันทึก/ยกเลิกได้เฉพาะ FIN-06/FIN-07 ที่บัญชีผูกกับตาแสง (scope ระดับตาแสง) และยกเลิกได้เฉพาะรายการของตาแสงตัวเอง —
 * จอแค่ซ่อนปุ่ม server ตรวจซ้ำทุกครั้ง · แก้รายการไม่ได้ (ผิด = ยกเลิกแล้วบันทึกใหม่) · ยกเลิกไม่ลบแถว
 */

const DESC_MAX = 500;
const REF_MAX = 100;
const REASON_MAX = 500;

async function fetchExpenses(query: FinanceFiltersState["query"], includeVoided: boolean) {
  const { data, error, response } = await api.GET("/v1/admin/expenses", {
    params: { query: { ...query, include_voided: includeVoided } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export function ExpensesPage() {
  const f = useFinanceFilters();
  const placeName = usePlaceName(f.place);
  const showVoided = f.params.get("voided") === "1";
  const scope = getCurrentScope();
  const canRecord = hasPerm(PERM.expenseRecord) && scope?.level === "tasaeng";
  const [creating, setCreating] = useState(false);
  const [voiding, setVoiding] = useState<Expense | null>(null);

  const list = useQuery({
    queryKey: ["finance", "expenses", f.key, showVoided],
    queryFn: () => fetchExpenses(f.query, showVoided),
  });
  const items = list.data?.items ?? [];
  const active = items.filter((e) => !e.voided_at);
  const total = active.reduce((s, e) => s + e.amount_lak, 0);
  const canVoid = (e: Expense) => canRecord && !e.voided_at && e.tasaeng_id === scope?.tasaeng_id;

  return (
    <div>
      <AdminTopline
        title="ລາຍຈ່າຍ"
        subtitle={`${placeName} · ${f.periodText}`}
        actions={
          canRecord ? (
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              <Plus size={14} /> ບັນທຶກລາຍຈ່າຍ
            </button>
          ) : null
        }
      />
      {canRecord ? null : <p className="dx-cap fin-cap">ບັນທຶກລາຍຈ່າຍໄດ້ສະເພາະການເງິນ ຫຼື ຫົວໜ້າຕາແສງ ຂອງຕາແສງນັ້ນ</p>}

      <FinanceFilters f={f} />

      <label className="switch switch--row fin-gap-b">
        <input
          type="checkbox"
          checked={showVoided}
          onChange={(e) => f.go({ extra: { voided: e.target.checked ? "1" : null } })}
        />
        <span className="switch__track" aria-hidden="true" />
        <span className="switch__label">ສະແດງລາຍການທີ່ຍົກເລີກແລ້ວ</span>
      </label>

      {list.data?.truncated ? (
        <div className="alert-warning fin-gap-b">
          ມີລາຍການຫຼາຍກວ່າ 500 ແຖວ — ສະແດງສະເພາະ 500 ລາຍການຫຼ້າສຸດ ກະລຸນາເລືອກຊ່ວງວັນທີໃຫ້ແຄບລົງ
        </div>
      ) : null}
      {list.error ? <div className="alert-error fin-gap-b">ໂຫຼດລາຍການບໍ່ສຳເລັດ: {list.error.message}</div> : null}

      <div className="table-scroll">
        <table className="data-table dx-table" aria-label="ລາຍການລາຍຈ່າຍ">
          <thead>
            <tr>
              <th>ວັນທີ</th>
              <th>ຕາແສງ</th>
              <th>ໝວດ</th>
              <th>ລາຍລະອຽດ</th>
              <th>ເລກທີໃບບິນ</th>
              <th className="is-right">ຈຳນວນເງິນ (ກີບ)</th>
              <th>ຜູ້ບັນທຶກ</th>
              <th aria-label="ຄຳສັ່ງ" />
            </tr>
          </thead>
          <tbody>
            {list.isPending ? (
              <tr>
                <td colSpan={8} className="data-table__empty">
                  ກຳລັງໂຫຼດ…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="data-table__empty">
                  ບໍ່ມີລາຍຈ່າຍໃນຊ່ວງນີ້
                </td>
              </tr>
            ) : (
              items.map((e) => {
                const strike = e.voided_at ? "fin-voided" : undefined;
                return (
                  <tr key={e.id}>
                    <td className={["num", strike].filter(Boolean).join(" ")}>{dmy(e.spent_on)}</td>
                    <td className={strike}>{e.tasaeng_name_lo}</td>
                    <td className={strike}>{EXPENSE_CATEGORY_LO[e.category]}</td>
                    <td>
                      <span className={strike}>{e.description}</span>
                      {e.voided_at ? (
                        <div className="caption">
                          ຍົກເລີກແລ້ວ: {e.void_reason}
                          {e.voided_by_name ? ` · ${e.voided_by_name}` : ""}
                        </div>
                      ) : null}
                    </td>
                    <td className={["num", strike].filter(Boolean).join(" ")}>{e.reference || "—"}</td>
                    <td className={["num is-right", strike].filter(Boolean).join(" ")}>{num(e.amount_lak)}</td>
                    <td className={strike}>{e.created_by_name}</td>
                    <td>
                      {canVoid(e) ? (
                        <button type="button" className="link-action" onClick={() => setVoiding(e)}>
                          ຍົກເລີກ
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
            {active.length > 0 ? (
              <tr className="dx-table__total">
                <td colSpan={5}>ລວມ ({num(active.length)} ລາຍການ{showVoided ? " · ບໍ່ນັບທີ່ຍົກເລີກ" : ""})</td>
                <td className="num is-right">{num(total)}</td>
                <td colSpan={2} />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="dx-cap fin-cap">{EXPENSE_CAPTION}</p>

      {creating ? <CreateExpenseModal nowMs={f.nowMs} onClose={() => setCreating(false)} /> : null}
      {voiding ? <VoidExpenseModal expense={voiding} onClose={() => setVoiding(null)} /> : null}
    </div>
  );
}

function CreateExpenseModal({ nowMs, onClose }: { nowMs: number; onClose: () => void }) {
  const qc = useQueryClient();
  // วันนี้ตามเวลาลาว — server ปฏิเสธวันในอนาคตอยู่แล้ว ช่องวันที่จึงตั้ง max ให้ตรงกัน
  const today = new Date(nowMs + 7 * 3_600_000).toISOString().slice(0, 10);
  const [spentOn, setSpentOn] = useState(today);
  const [category, setCategory] = useState<ExpenseCategory | "">("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [touched, setTouched] = useState(false);

  const amountNum = amount ? Number(amount) : 0;
  const problems = {
    spentOn: !spentOn ? "ກະລຸນາເລືອກວັນທີ" : spentOn > today ? "ເລືອກວັນທີໃນອະນາຄົດບໍ່ໄດ້" : null,
    category: category ? null : "ກະລຸນາເລືອກໝວດ",
    amount: amountNum >= 1 ? null : "ກະລຸນາໃສ່ຈຳນວນເງິນ",
    description: description.trim() ? null : "ກະລຸນາໃສ່ລາຍລະອຽດ",
  };
  const invalid = Object.values(problems).some((p) => p !== null);

  const save = useMutation({
    mutationFn: async (body: ExpenseWrite) => {
      const { data, error, response } = await api.POST("/v1/admin/expenses", { body, headers: authHeaders() });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (invalid || !category) return;
    const ref = reference.trim();
    save.mutate({
      spent_on: spentOn,
      category,
      amount_lak: amountNum,
      description: description.trim(),
      ...(ref ? { reference: ref } : {}),
    });
  }

  const err = (k: keyof typeof problems) =>
    touched && problems[k] ? <p className="field-error">{problems[k]}</p> : null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ບັນທຶກລາຍຈ່າຍ">
      <form className="modal" onSubmit={submit} noValidate>
        <div className="modal__header">
          <h2 className="modal__title">ບັນທຶກລາຍຈ່າຍ</h2>
          <button type="button" className="btn-secondary btn-sm" aria-label="ປິດ" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="modal__grid">
          <div>
            <label htmlFor="exp-date" className="form-field-label">
              ວັນທີຈ່າຍ<span className="required"> *</span>
            </label>
            <input
              id="exp-date"
              type="date"
              className="input-field num"
              max={today}
              value={spentOn}
              onChange={(e) => setSpentOn(e.target.value)}
            />
            {err("spentOn")}
          </div>
          <div>
            <label htmlFor="exp-cat" className="form-field-label">
              ໝວດ<span className="required"> *</span>
            </label>
            <select
              id="exp-cat"
              className="input-field"
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory | "")}
            >
              <option value="">ເລືອກໝວດ</option>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {EXPENSE_CATEGORY_LO[c]}
                </option>
              ))}
            </select>
            {err("category")}
          </div>
          <div>
            <label htmlFor="exp-amount" className="form-field-label">
              ຈຳນວນເງິນ (ກີບ)<span className="required"> *</span>
            </label>
            {/* รับแต่ตัวเลขแล้วแสดงแบบมีตัวคั่นหลักพัน — เก็บเป็นสตริงตัวเลขล้วน จำกัด 15 หลัก (ไม่เกิน Number.MAX_SAFE_INTEGER) */}
            <input
              id="exp-amount"
              className="input-field num"
              inputMode="numeric"
              autoComplete="off"
              placeholder="0"
              value={amount ? num(Number(amount)) : ""}
              onChange={(e) => setAmount(e.target.value.replace(/\D+/g, "").replace(/^0+/, "").slice(0, 15))}
            />
            {amountNum > 0 ? <p className="caption num">{fullLAK(amountNum)}</p> : null}
            {err("amount")}
          </div>
          <div>
            <label htmlFor="exp-ref" className="form-field-label">
              ເລກທີໃບບິນ / ໃບຮັບເງິນ
            </label>
            <input
              id="exp-ref"
              className="input-field"
              maxLength={REF_MAX}
              placeholder="ບໍ່ບັງຄັບ"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
        </div>
        <div className="fin-gap-b">
          <label htmlFor="exp-desc" className="form-field-label">
            ລາຍລະອຽດ<span className="required"> *</span>
          </label>
          <textarea
            id="exp-desc"
            className="input-field"
            rows={3}
            maxLength={DESC_MAX}
            placeholder="ເຊັ່ນ: ຄ່າໄຟຟ້າ ເດືອນ 9"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {err("description")}
          <p className="caption">ບັນທຶກແລ້ວແກ້ໄຂບໍ່ໄດ້ — ຖ້າຜິດ ໃຫ້ຍົກເລີກລາຍການແລ້ວບັນທຶກໃໝ່</p>
        </div>

        {save.error ? <div className="alert-error fin-gap-b">{save.error.message}</div> : null}

        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            ປິດ
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກ"}
          </button>
        </div>
      </form>
    </div>
  );
}

function VoidExpenseModal({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const voidIt = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/v1/admin/expenses/{id}/void", {
        params: { path: { id: expense.id } },
        body: { reason: reason.trim() },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    },
  });

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ຍົກເລີກລາຍຈ່າຍ">
      <form
        className="modal modal--sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim()) voidIt.mutate();
        }}
      >
        <div className="modal__header">
          <h2 className="modal__title">ຍົກເລີກລາຍຈ່າຍນີ້?</h2>
          <p className="caption">
            {dmy(expense.spent_on)} · {EXPENSE_CATEGORY_LO[expense.category]} ·{" "}
            <span className="num">{fullLAK(expense.amount_lak)}</span>
          </p>
        </div>
        <p className="body-sm fin-gap-b">
          ລາຍການຈະບໍ່ຖືກລຶບ ແຕ່ຈະບໍ່ນັບໃນລາຍງານອີກ ແລະ ກັບຄືນບໍ່ໄດ້
        </p>
        <div className="fin-gap-b">
          <label htmlFor="void-reason" className="form-field-label">
            ເຫດຜົນ<span className="required"> *</span>
          </label>
          <textarea
            id="void-reason"
            className="input-field"
            rows={3}
            maxLength={REASON_MAX}
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {voidIt.error ? <div className="alert-error fin-gap-b">{voidIt.error.message}</div> : null}
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            ປິດ
          </button>
          <button type="submit" className="btn-danger" disabled={!reason.trim() || voidIt.isPending}>
            ຢືນຢັນຍົກເລີກ
          </button>
        </div>
      </form>
    </div>
  );
}
